import { useCallback, useMemo } from "react";

import { compareFractionalOrder } from "@/lib/compare-fractional-order";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getPhaseListForWorkspaceQueryDocument } from "./phase.gql";

export interface UseGetPhaseListForWorkspaceProps {
  workspaceId?: string | undefined;
  enabled?: boolean;
}

export const useGetPhaseListForWorkspace = ({ workspaceId, enabled = true }: UseGetPhaseListForWorkspaceProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.phases.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }
      const client = createOperationClient("GetPhaseListForWorkspace");
      const data = await client.request(getPhaseListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });
      return data.phaseListForWorkspace;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const phaseList = useMemo(() => {
    const maybePhaseList = queryResult.data ?? [];
    if (!maybePhaseList) {
      return [];
    }

    return [...maybePhaseList].sort((a, b) => compareFractionalOrder(a.order, b.order));
  }, [queryResult.data]);

  const findPhaseById = useCallback(
    (id: string) => {
      return phaseList.find((phase) => phase.id === id);
    },
    [phaseList],
  );

  return {
    phaseList,
    findPhaseById,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
